import { Box, Button, Flex, Grid, Stack, Text } from "@chakra-ui/react";
import { useNotification } from "@refinedev/core";
import { Clock3, KeyRound, LogOut, Server, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { MetricCard, PageHeader, Panel, StatusPill } from "../components/ui";
import { useAuthSession } from "../providers/auth-session";
import { apiBaseUrl } from "../providers/api-client";

export function SettingsPage() {
  const { session, logout } = useAuthSession();
  const [signingOut, setSigningOut] = useState(false);
  const { open } = useNotification();
  const expiresAt = session?.expiresAt ? new Date(session.expiresAt) : undefined;

  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      window.location.replace("/login");
    } catch (error) {
      open?.({
        type: "error",
        message: "Could not sign out",
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Stack gap="5">
      <PageHeader
        title="Settings"
        description="Secure session and server connection details. Broker credentials remain server-side."
        actions={<StatusPill tone="positive">SESSION ACTIVE</StatusPill>}
      />

      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard icon={ShieldCheck} label="Access" value="Authorized" detail="HttpOnly server session" positive />
        <MetricCard icon={Clock3} label="Session expires" value={expiresAt ? expiresAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Server managed"} detail={expiresAt ? expiresAt.toLocaleDateString() : "Validated by server"} />
        <MetricCard icon={Server} label="API channel" value={apiBaseUrl} detail="Credentialed requests only" />
        <MetricCard icon={KeyRound} label="Secret storage" value="Not stored" detail="Browser keeps only an HttpOnly cookie" positive />
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "minmax(0,1.25fr) minmax(320px,.75fr)" }} gap="4" alignItems="start">
        <Panel icon={ShieldCheck} title="Session security">
          <Stack gap="3">
            <SecurityRow label="API access" value="Protected" detail="Every trading, portfolio, infrastructure, audit and backtest endpoint requires this session." />
            <SecurityRow label="Browser storage" value="No raw secret" detail="The admin secret is discarded immediately after the server creates the signed session." />
            <SecurityRow label="Cookie policy" value="HttpOnly · SameSite Strict" detail="Application code cannot read the session token and cross-site requests cannot reuse it." />
            <SecurityRow label="Server restart" value="Session survives" detail="The signed session remains valid until its expiry or an explicit sign out." />
          </Stack>
        </Panel>

        <Panel icon={LogOut} title="Account session">
          <Stack gap="4">
            <Flex p="3.5" bg="rgba(16,185,129,.08)" borderWidth="1px" borderColor="rgba(16,185,129,.24)" borderRadius="4px" gap="3" align="center">
              <Flex boxSize="34px" bg="rgba(16,185,129,.12)" color="status.positive" borderRadius="4px" align="center" justify="center" flexShrink="0">
                <Box as={ShieldCheck} boxSize="4" />
              </Flex>
              <Box>
                <Text fontSize="xs" fontWeight="750">This browser is authorized</Text>
                <Text mt="0.5" fontSize="10px" color="fg.muted">No secret input is required while the session remains valid.</Text>
              </Box>
            </Flex>
            <Button variant="outline" color="status.negative" onClick={signOut} disabled={signingOut}>
              <Box as={LogOut} boxSize="4" />
              {signingOut ? "Signing out…" : "Sign out this browser"}
            </Button>
          </Stack>
        </Panel>
      </Grid>

      <Panel icon={ShieldCheck} title="Safety boundary">
        <Text fontSize="xs" color="fg.muted" lineHeight="1.7">
          Authentication grants access to the control center. cTrader account environment, access rights, reconciliation, leverage limits, live-order permissions and the global kill switch are still validated independently by the server.
        </Text>
      </Panel>
    </Stack>
  );
}

function SecurityRow({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Flex p="3" bg="bg.subtle" borderWidth="1px" borderColor="border.subtle" borderRadius="4px" justify="space-between" align={{ base: "flex-start", sm: "center" }} direction={{ base: "column", sm: "row" }} gap="2">
      <Box>
        <Text fontSize="xs" fontWeight="700">{label}</Text>
        <Text mt="0.5" fontSize="10px" color="fg.muted">{detail}</Text>
      </Box>
      <StatusPill tone="positive">{value}</StatusPill>
    </Flex>
  );
}
